import { Link } from "react-router-dom";
import { ArrowRight, Check, Phone } from "lucide-react";
import SEO from "../components/SEO";
import EditorialMarquee from "../components/EditorialMarquee";
import StatusBadge from "../components/StatusBadge";
import { FadeUp, MaskedLines, SectionLabel } from "../components/Reveal";
import { IMG, PHONE_DISPLAY, PHONE_TEL } from "../data/site";

const CONTENT = {
    rideau: {
        name: "rideau métallique",
        price: "sur devis",
        intro: (z) =>
            `Rideau métallique bloqué, moteur en panne, lame cassée ${z.label} : votre commerce ne peut pas rester fermé. Nos techniciens interviennent 24h/24 pour débloquer et réparer votre rideau de fer.`,
        includes: [
            "Déblocage de rideau métallique coincé",
            "Réparation et remplacement de moteur",
            "Remplacement de lames et d'axes",
            "Ouverture de rideau après effraction",
            "Fabrication et installation sur mesure",
            "Entretien préventif pour commerces",
        ],
        sections: [
            {
                h: "Un rideau bloqué ne peut pas attendre",
                ps: [
                    "Un rideau métallique bloqué en position fermée empêche l'ouverture de votre boutique ; bloqué ouvert, il laisse votre local sans protection. Dans les deux cas, nos équipes interviennent en urgence, de jour comme de nuit.",
                    "Notre service d'ouverture de rideau métallique fonctionne 24h/24 et 7j/7, y compris le dimanche et les jours fériés, pour sécuriser votre activité au plus vite.",
                ],
                img: IMG("serrurier-ouverture-de-porte.webp"),
            },
            {
                h: "De la réparation à la fabrication sur mesure",
                ps: [
                    "Remplacement de lames endommagées, d'axes, de ressorts, de serrures de rideau ou de motorisation complète : nous réparons toutes les configurations, manuelles comme motorisées.",
                    "Pour les commerces et locaux professionnels, nous assurons également la fabrication, l'installation et la pose de rideaux métalliques sur mesure, ainsi qu'un entretien préventif qui évite les pannes.",
                ],
            },
        ],
    },
    volet: {
        name: "volet roulant",
        price: "sur devis",
        intro: (z) =>
            `Volet roulant bloqué, manivelle cassée, moteur en panne ${z.label} : nos techniciens dépannent tous les volets roulants, manuels comme électriques, dans la journée.`,
        includes: [
            "Déblocage de volet roulant coincé",
            "Réparation ou remplacement de moteur",
            "Remplacement de lames et de tablier",
            "Réparation de manivelle et de treuil",
            "Motorisation de volets manuels",
            "Entretien préventif",
        ],
        sections: [
            {
                h: "Déblocage de volet roulant, manuel ou électrique",
                ps: [
                    "Un volet roulant bloqué en position fermée plonge votre pièce dans le noir et bloque votre accès à l'extérieur ; bloqué ouvert, il expose votre logement. Nous intervenons rapidement pour diagnostiquer la panne : sangle, manivelle, treuil ou moteur.",
                    "Notre service de dépannage fonctionne 24h/24 et 7j/7, y compris le week-end et les jours fériés.",
                ],
                img: IMG("entretien-regulier-serrurerie.webp"),
            },
            {
                h: "Réparation et motorisation",
                ps: [
                    "Lames cassées, tablier désaxé, moteur hors service : nous réparons ou remplaçons les pièces défectueuses avec du matériel de qualité, et nous pouvons motoriser vos volets manuels pour plus de confort.",
                    "Chaque intervention est précédée d'un devis gratuit et détaillé : le prix annoncé est le prix payé.",
                ],
            },
        ],
    },
};

export default function ServiceDept({ type, zone }) {
    const c = CONTENT[type];
    if (!c) return null;
    const title = `Dépannage ${c.name} ${zone.label}`;
    const path = `/depannage-${type === "rideau" ? "rideau-metallique" : "volet-roulant"}-${zone.slug}`;

    return (
        <div data-testid="service-dept-page">
            <SEO
                title={`${title.charAt(0).toUpperCase() + title.slice(1)} — Urgence 24/7 · Devis Gratuit`}
                description={`${title.charAt(0).toUpperCase() + title.slice(1)} : déblocage, réparation, moteur et fabrication sur mesure. Intervention rapide 24h/24, devis gratuit et transparent. ☎ 06 25 25 86 19`}
                path={path}
            />

            <section className="grain relative overflow-hidden bg-midnight py-10 text-white md:py-16">
                <div className="pointer-events-none absolute -right-24 top-0 h-80 w-80 rounded-full bg-emergency/20 blur-3xl" />
                <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
                    <nav className="mb-5 flex flex-wrap items-center gap-2 text-xs font-semibold uppercase tracking-wider text-white/50" data-testid="breadcrumb">
                        <Link to="/" className="transition-colors hover:text-gold">Accueil</Link>
                        <span>/</span>
                        <span className="capitalize">{c.name}</span>
                        <span>/</span>
                        <span className="text-gold capitalize">{zone.label}</span>
                    </nav>
                    <SectionLabel dark>Urgence commerces & particuliers</SectionLabel>
                    <StatusBadge className="mt-4" />
                    <h1 className="mt-3 max-w-4xl font-display text-4xl font-extrabold leading-[1.05] tracking-tighter sm:text-5xl">
                        <MaskedLines delay={0.15} lines={[title]} />
                    </h1>
                    <FadeUp delay={0.5}>
                        <p className="mt-5 max-w-2xl leading-relaxed text-white/70">{c.intro(zone)}</p>
                        <a
                            href={PHONE_TEL}
                            data-testid="service-dept-call-button"
                            className="mt-7 inline-flex items-center gap-3 bg-emergency px-8 py-4 text-lg font-bold text-white transition-colors hover:bg-emergency-dark"
                        >
                            <Phone className="h-5 w-5" /> {PHONE_DISPLAY}
                        </a>
                    </FadeUp>
                </div>
            </section>

            <EditorialMarquee dark={false} />

            <section className="mx-auto max-w-7xl px-4 py-14 sm:px-6 md:py-20 lg:px-8">
                <div className="grid gap-12 lg:grid-cols-[1fr_1.5fr] lg:gap-20">
                    <FadeUp>
                        <SectionLabel>Prestation complète</SectionLabel>
                        <h2 className="mt-4 font-display text-3xl font-extrabold tracking-tight md:text-4xl">
                            Ce que comprend notre intervention
                        </h2>
                        <p className="mt-5 leading-relaxed text-midnight/70">
                            Chaque intervention est précédée d'un devis gratuit et sans engagement. Le prix annoncé
                            est le prix payé : aucune majoration cachée.
                        </p>
                    </FadeUp>
                    <div className="grid gap-px rounded-xl bg-midnight/10 sm:grid-cols-2" data-testid="service-dept-includes">
                        {c.includes.map((item, i) => (
                            <FadeUp key={item} delay={(i % 6) * 0.04} className="bg-white">
                                <div className="flex h-full items-center gap-3 px-5 py-4 text-sm font-semibold text-midnight/80">
                                    <Check className="h-4 w-4 shrink-0 text-emergency" /> {item}
                                </div>
                            </FadeUp>
                        ))}
                    </div>
                </div>

                <div className="mt-16 space-y-16">
                    {c.sections.map((s, i) => (
                        <div key={s.h} className="grid items-center gap-10 lg:grid-cols-2 lg:gap-16">
                            <FadeUp className={i % 2 === 1 ? "lg:order-2" : ""}>
                                <div className="flex aspect-[4/3] w-full items-center justify-center rounded-xl bg-midnight/5 p-4 shadow-[0_24px_60px_-20px_rgba(10,17,40,0.28)]">
                                    <img
                                        src={s.img}
                                        alt={title}
                                        className="h-full w-full object-contain"
                                        width="800"
                                        height="600"
                                        loading="lazy"
                                    />
                                </div>
                            </FadeUp>
                            <div className={i % 2 === 1 ? "lg:order-1" : ""}>
                                <FadeUp>
                                    <h2 className="font-display text-3xl font-extrabold tracking-tight md:text-4xl">{s.h}</h2>
                                    {s.ps.map((p, j) => (
                                        <p key={j} className="mt-4 leading-relaxed text-midnight/70">{p}</p>
                                    ))}
                                </FadeUp>
                            </div>
                        </div>
                    ))}
                </div>
            </section>


            {/* FAQ */}
            <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6 md:py-20 lg:px-8">
                <FadeUp>
                    <SectionLabel>Questions fréquentes</SectionLabel>
                    <h2 className="mt-4 font-display text-3xl font-extrabold tracking-tight md:text-4xl">
                        {`FAQ — ${c.name} ${zone.label}`}
                    </h2>
                </FadeUp>
                <div className="mt-8 divide-y divide-midnight/10 border border-midnight/10 bg-white">
                    {[
                        { q: `Quel est le délai d'intervention pour un ${c.name} ${zone.label} ?`, a: `Nos techniciens interviennent en moyenne en 30 minutes ${zone.label} pour tout dépannage ${c.name}. Le service fonctionne 24h/24 et 7j/7, week-ends et jours fériés inclus.` },
                        { q: `Quel est le prix d'une intervention ${c.name} ?`, a: `Le tarif dépend de la nature de la panne et du matériel à remplacer. Un devis gratuit est établi par téléphone avant toute intervention. Le prix vous est communiqué et validé avant que notre technicien commence les travaux.` },
                        { q: `Intervenez-vous en urgence la nuit pour un ${c.name} bloqué ?`, a: `Oui, notre service d'urgence ${c.name} fonctionne 24h/24 et 7j/7 ${zone.label}. Un rideau ou volet bloqué en position ouverte laisse votre local sans protection — nous traitons ce type d'urgence en priorité.` },
                    ].map((item, i) => (
                        <FadeUp key={i} delay={i * 0.05}>
                            <details className="group">
                                <summary className="flex cursor-pointer items-center justify-between gap-4 px-6 py-5 font-display text-base font-bold tracking-tight marker:content-none hover:bg-cloud">
                                    {item.q}
                                    <span className="shrink-0 font-display text-xl text-emergency transition-transform duration-200 group-open:rotate-45">+</span>
                                </summary>
                                <div className="px-6 pb-5 text-sm leading-relaxed text-midnight/70">{item.a}</div>
                            </details>
                        </FadeUp>
                    ))}
                </div>
            </section>

            <section className="grain bg-midnight py-14 text-white md:py-16">
                <div className="mx-auto flex max-w-7xl flex-wrap items-center justify-between gap-8 px-4 sm:px-6 lg:px-8">
                    <div>
                        <h2 className="font-display text-3xl font-extrabold tracking-tight md:text-4xl">
                            {`Urgence ${c.name} ? Intervention rapide.`}
                        </h2>
                        <p className="mt-3 text-white/60">24h/24 — 7j/7. Devis gratuit au téléphone.</p>
                    </div>
                    <div className="flex flex-wrap gap-3">
                        <a
                            href={PHONE_TEL}
                            data-testid="service-dept-bottom-call-button"
                            className="flex items-center gap-3 bg-emergency px-8 py-4 text-lg font-bold text-white transition-colors hover:bg-emergency-dark"
                        >
                            <Phone className="h-5 w-5" /> {PHONE_DISPLAY}
                        </a>
                        <Link
                            to={type === "rideau" ? "/rideau-metallique" : "/devis-contact"}
                            className="flex items-center gap-2 border border-white/30 px-7 py-4 font-bold text-white transition-colors hover:border-gold hover:bg-gold hover:text-midnight"
                        >
                            En savoir plus <ArrowRight className="h-4 w-4" />
                        </Link>
                    </div>
                </div>
            </section>
        </div>
    );
}
